/**
 * passkey-budget WP3: the approval tray ("3 actions need your passkey — Approve all").
 *
 * Every risky command (a risky ask, a production release, a secret allow, a landing outside a
 * grant, a deletion…) waits here unsigned. Approving signs the chosen items NOW (each with its own
 * device signature, nonce and ≤ 5 min expiry) and takes ONE passkey ceremony over the batch of
 * their step-up challenges, so each daemon verifies only its own item. The sheet lists exactly the
 * items being signed (the same array the batch is built from), with production releases called
 * out. "Later" keeps items waiting; "Cancel" sends nothing.
 */
import type { SignedCommand } from '@viko/crypto'
import { MAX_TRAY_BATCH, SignError, type TrayRequest } from './signer.js'

export interface TrayItem extends TrayRequest {
  id: string
  addedAt: number
}

interface Pending {
  item: TrayItem
  resolve: (s: SignedCommand) => void
  reject: (e: Error) => void
}

export interface TraySnapshot {
  items: readonly TrayItem[]
  /** The sheet is showing (a new item opens it; Later closes it, items stay). */
  open: boolean
  busy: boolean
  error?: string
  /** How many of `items` are production releases (called out in the sheet). */
  production: number
}

export class ApprovalTray {
  private pending: Pending[] = []
  private state: TraySnapshot = { items: [], open: false, busy: false, production: 0 }
  private readonly listeners = new Set<() => void>()
  private seq = 0

  constructor(
    private readonly sign: (reqs: readonly TrayRequest[]) => Promise<SignedCommand[]>,
    private readonly now: () => number = Date.now,
  ) {}

  snapshot = () => this.state
  subscribe = (fn: () => void) => {
    this.listeners.add(fn)
    return () => void this.listeners.delete(fn)
  }
  /** `error: null` clears the error. */
  private emit(patch: { open?: boolean; busy?: boolean; error?: string | null } = {}) {
    const items = this.pending.map((p) => p.item)
    const error = patch.error === undefined ? this.state.error : (patch.error ?? undefined)
    this.state = {
      items,
      open: items.length > 0 && (patch.open ?? this.state.open),
      busy: patch.busy ?? this.state.busy,
      production: items.filter((i) => i.production).length,
      ...(error ? { error } : {}),
    }
    for (const fn of this.listeners) fn()
  }

  /** The signer's port: queue a risky command; resolves with it signed once approved. */
  request = (r: TrayRequest): Promise<SignedCommand> =>
    new Promise<SignedCommand>((resolve, reject) => {
      const item: TrayItem = { ...r, id: `tray-${++this.seq}`, addedAt: this.now() }
      this.pending.push({ item, resolve, reject })
      this.emit({ open: true })
    })

  /**
   * Approve the given items (default: all, up to the batch cap) with ONE passkey ceremony.
   * A dismissed passkey keeps them waiting (the sheet shows why).
   */
  async approve(ids?: readonly string[]): Promise<void> {
    if (this.state.busy) return
    const chosen = this.pending
      .filter((p) => !ids || ids.includes(p.item.id))
      .slice(0, MAX_TRAY_BATCH)
    if (!chosen.length) return
    this.emit({ busy: true, error: null })
    let signed: SignedCommand[]
    try {
      signed = await this.sign(chosen.map((p) => p.item))
    } catch (e) {
      this.emit({ busy: false, error: (e as Error)?.message ?? 'The passkey request failed.' })
      throw e
    }
    this.pending = this.pending.filter((p) => !chosen.includes(p))
    chosen.forEach((p, i) => {
      p.resolve(signed[i] as SignedCommand)
    })
    this.emit({ busy: false, error: null })
  }

  /** Send nothing for these items (default: all). */
  decline(ids?: readonly string[]) {
    const out = this.pending.filter((p) => !ids || ids.includes(p.item.id))
    this.pending = this.pending.filter((p) => !out.includes(p))
    for (const p of out) p.reject(new SignError('cancelled', 'Cancelled — nothing was sent.'))
    this.emit({ error: null })
  }

  /** Close the sheet; everything stays waiting for the next approval. */
  later() {
    this.emit({ open: false, error: null })
  }

  show() {
    if (this.pending.length) this.emit({ open: true })
  }
}
